<template>
    <div class="max-w-4xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Thread
            </h1>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
            ></div>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <p class="text-sm text-danger-700 dark:text-danger-300">
                {{ error }}
            </p>
        </div>

        <template v-if="!loading && thread">
            <!-- Thread Post -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div class="flex items-start justify-between gap-3 mb-3">
                    <div class="flex items-center gap-2 flex-wrap">
                        <span
                            v-if="thread.is_pinned"
                            class="inline-flex items-center text-xs font-semibold text-warning-600 dark:text-warning-400"
                        >
                            <Pin class="w-3.5 h-3.5 mr-1" />
                            Pinned
                        </span>
                        <span
                            v-if="thread.is_locked"
                            class="inline-flex items-center text-xs font-semibold text-gray-500"
                        >
                            <Lock class="w-3.5 h-3.5 mr-1" />
                            Locked
                        </span>
                        <span
                            v-if="thread.status !== 'published'"
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200"
                        >
                            {{ thread.status }}
                        </span>
                    </div>
                    <div v-if="isModerator" class="flex items-center gap-1">
                        <button
                            @click="togglePin"
                            :title="
                                thread.is_pinned ? 'Unpin Thread' : 'Pin Thread'
                            "
                            class="inline-flex items-center justify-center p-2 text-warning-600 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded-md transition-colors mr-1"
                        >
                            <Pin class="w-4 h-4" />
                        </button>
                        <button
                            @click="toggleLock"
                            :title="
                                thread.is_locked
                                    ? 'Unlock Thread'
                                    : 'Lock Thread'
                            "
                            class="inline-flex items-center justify-center p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors mr-1"
                        >
                            <Lock class="w-4 h-4" />
                        </button>
                        <button
                            v-if="thread.status === 'draft'"
                            @click="approve"
                            title="Approve Post"
                            class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded-md transition-colors mr-1"
                        >
                            <CheckCircle2 class="w-4 h-4" />
                        </button>
                        <button
                            v-if="thread.status === 'published'"
                            @click="hide"
                            title="Hide Post"
                            class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-md transition-colors"
                        >
                            <EyeOff class="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <h2
                    class="text-lg font-semibold text-gray-900 dark:text-white mb-2"
                >
                    {{ thread.title }}
                </h2>

                <p
                    class="text-sm text-gray-900 dark:text-white whitespace-pre-line mb-3"
                >
                    {{ thread.content }}
                </p>

                <div
                    class="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500"
                >
                    <span>
                        {{ thread.user?.name || "-" }} ·
                        {{ formatDate(thread.created_at) }} ·
                        {{ thread.view_count }} views
                    </span>
                    <button
                        @click="like(thread)"
                        class="inline-flex items-center gap-1 hover:text-danger-500 transition-colors"
                        :class="thread.is_liked_by_me ? 'text-danger-500' : ''"
                        title="Like"
                    >
                        <Heart
                            class="w-4 h-4"
                            :class="
                                thread.is_liked_by_me ? 'fill-danger-500' : ''
                            "
                        />
                        {{ thread.like_count }}
                    </button>
                </div>
            </div>

            <!-- Replies -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3
                    class="text-sm font-semibold text-gray-900 dark:text-white mb-4"
                >
                    Replies ({{ thread.replies?.length || 0 }})
                </h3>

                <EmptyState
                    v-if="(thread.replies?.length || 0) === 0"
                    icon="MessagesSquareIcon"
                    title="No replies yet"
                    description="Start the conversation by replying below."
                />

                <div v-else class="space-y-4">
                    <div
                        v-for="reply in thread.replies"
                        :key="reply.id"
                        class="border-l-2 border-primary-200 dark:border-primary-800 pl-4 py-1"
                        :style="replyIndentStyle(reply)"
                    >
                        <p
                            class="text-xs text-gray-400 dark:text-gray-500 mb-1"
                        >
                            {{ reply.user?.name || "-" }} ·
                            {{ formatDate(reply.created_at) }}
                            <span
                                v-if="reply.status !== 'published'"
                                class="ml-1 text-warning-600 dark:text-warning-400"
                            >
                                ({{ reply.status }})
                            </span>
                        </p>
                        <p
                            class="text-sm text-gray-900 dark:text-white whitespace-pre-line"
                        >
                            {{ reply.content }}
                        </p>
                        <div class="flex items-center gap-3 mt-1">
                            <button
                                @click="like(reply)"
                                class="inline-flex items-center gap-1 text-xs hover:text-danger-500 transition-colors"
                                :class="
                                    reply.is_liked_by_me
                                        ? 'text-danger-500'
                                        : 'text-gray-400'
                                "
                                title="Like"
                            >
                                <Heart
                                    class="w-3.5 h-3.5"
                                    :class="
                                        reply.is_liked_by_me
                                            ? 'fill-danger-500'
                                            : ''
                                    "
                                />
                                {{ reply.like_count }}
                            </button>
                            <button
                                v-if="!thread.is_locked || isModerator"
                                @click="startReplyTo(reply)"
                                class="text-xs text-gray-400 hover:text-primary-500 transition-colors"
                                title="Reply"
                            >
                                Reply
                            </button>
                            <button
                                v-if="
                                    isModerator && reply.status !== 'published'
                                "
                                @click="approveReply(reply)"
                                class="text-xs text-success-600 hover:text-success-500 transition-colors"
                                title="Approve Post"
                            >
                                Approve Post
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Reply Composer -->
            <div
                v-if="canReply"
                class="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
            >
                <p
                    v-if="replyTo"
                    class="text-xs text-gray-500 dark:text-gray-400 mb-2"
                >
                    Replying to "{{ replyTo.content.slice(0, 50) }}..."
                    <button
                        @click="replyTo = null"
                        class="ml-2 text-danger-500 hover:text-danger-400"
                        title="Cancel Reply"
                    >
                        Cancel
                    </button>
                </p>
                <textarea
                    v-model="replyContent"
                    rows="3"
                    placeholder="Write a reply..."
                    class="form-input w-full"
                ></textarea>
                <div class="flex justify-end mt-3">
                    <button
                        @click="submitReply"
                        :disabled="replying || !replyContent.trim()"
                        class="btn btn-primary disabled:opacity-50"
                    >
                        {{ replying ? "Sending..." : "Send Reply" }}
                    </button>
                </div>
            </div>
            <div
                v-else-if="thread.is_locked"
                class="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-500 dark:text-gray-400 text-center"
            >
                This thread is locked. New replies are not allowed.
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Pin, Lock, Heart, CheckCircle2, EyeOff } from "@lucide/vue";
import EmptyState from "@/components/EmptyState.vue";
import forumApi, { ForumPost } from "@/api/forum";
import { useAuthStore } from "@/stores/authStore";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const threadId = route.params.threadId as string;
const forumId = (route.params.id as string) || "";

const thread = ref<ForumPost | null>(null);
const loading = ref(false);
const replying = ref(false);
const error = ref<string | null>(null);

const replyContent = ref("");
const replyTo = ref<ForumPost | null>(null);

const isModerator = computed(
    () =>
        authStore.hasAnyRole(["administrator", "super-administrator"]) ||
        authStore.user?.id === thread.value?.forum?.teacher?.user?.id,
);

const canReply = computed(() => {
    if (!thread.value) return false;
    if (thread.value.is_locked) return isModerator.value;
    return true;
});

// Depth lookup for visual nesting (flat list from API, indented by walking parent chain).
const depthById = computed(() => {
    const depths: Record<string, number> = {};
    const posts = thread.value?.replies || [];
    const byId: Record<string, ForumPost> = {};
    posts.forEach((post) => {
        byId[post.id] = post;
    });
    posts.forEach((post) => {
        let depth = 0;
        let cursor: ForumPost | undefined = post;
        while (cursor?.parent_id && byId[cursor.parent_id] && depth < 3) {
            depth++;
            cursor = byId[cursor.parent_id];
        }
        depths[post.id] = depth;
    });
    return depths;
});

function replyIndentStyle(reply: ForumPost) {
    const depth = Math.min(depthById.value[reply.id] || 0, 3);
    return { marginLeft: `${depth * 16}px` };
}

async function fetchThread() {
    loading.value = true;
    error.value = null;

    try {
        thread.value = await forumApi.getThread(threadId);
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to fetch thread";
    } finally {
        loading.value = false;
    }
}

async function submitReply() {
    replying.value = true;

    try {
        const parentId =
            replyTo.value?.parent_id || replyTo.value?.id || threadId;
        await forumApi.reply(parentId, replyContent.value);
        replyContent.value = "";
        replyTo.value = null;
        await fetchThread();
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to send reply";
    } finally {
        replying.value = false;
    }
}

function startReplyTo(reply: ForumPost) {
    replyTo.value = reply;
}

async function like(post: ForumPost) {
    try {
        const result = await forumApi.toggleLike(post.id);
        post.like_count = result.like_count;
        post.is_liked_by_me = result.is_liked_by_me;
    } catch (err: any) {
        console.error(err);
    }
}

async function approveReply(reply: any) {
    try {
        const updated = await forumApi.moderate(reply.id, "published");
        reply.status = updated.status ?? "published";
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to approve";
    }
}

async function togglePin() {
    try {
        const updated = await forumApi.togglePin(threadId);
        thread.value = { ...thread.value, ...updated };
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to toggle pin";
    }
}

async function toggleLock() {
    try {
        const updated = await forumApi.toggleLock(threadId);
        thread.value = { ...thread.value, ...updated };
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to toggle lock";
    }
}

async function approve() {
    try {
        const updated = await forumApi.moderate(threadId, "published");
        thread.value = { ...thread.value, ...updated };
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to approve";
    }
}

async function hide() {
    try {
        const updated = await forumApi.moderate(threadId, "hidden");
        thread.value = { ...thread.value, ...updated };
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to hide post";
    }
}

function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function goBack() {
    if (forumId) {
        router.push(`/forums/${forumId}`);
    } else if (thread.value?.forum_id) {
        router.push(`/forums/${thread.value.forum_id}`);
    } else {
        router.push("/forums");
    }
}

onMounted(() => {
    fetchThread();
});
</script>
